import { SurveyTypes } from '#shared/types/Survey'

export interface TestLandingContent {
    title: string
    subtitle: string
    icon: string
    iconBgClass: string
    duration: string
    questions: number
    description: string
    aboutText: string
    insights: string[]
    howItWorks: Array<{
        title: string
        description: string
    }>
    funFacts: string[]
    science: {
        basis: string
        researchers: string
        usage: string
    }
    critique?: {
        title: string
        description: string
    }
}

export const useTestLandingContent = () => {
    const content: Record<SurveyTypes, TestLandingContent> = {
        [SurveyTypes.BIG_FIVE]: {
            title: 'Big Five osobnostný test',
            subtitle: 'Preskúmaj 5 základných dimenzií osobnosti',
            icon: '🎭',
            iconBgClass: 'bg-blue-100',
            duration: '3',
            questions: 30,
            description:
                'Big Five je najpoužívanejší vedecký model osobnosti na svete. Tento test ti pomôže pochopiť tvoju osobnosť cez päť základných dimenzií, ktoré ovplyvňujú tvoje správanie, vzťahy a kariéru.',
            aboutText:
                'Model Big Five vznikol na základe desaťročí psychologického výskumu a je založený na analýze prirodzeného jazyka - ako ľudia popisujú seba a ostatných. Vďaka tomu zachytáva univerzálne dimenzie ľudskej osobnosti naprieč kultúrami.',
            insights: [
                'Tvoju úroveň otvorenosti voči novým zážitkom a kreativite',
                'Ako svedomitý a organizovaný si v práci a živote',
                'Či si introvert alebo extrovert a ako čerpáš energiu',
                'Tvoju mieru prívetivosti a spolupráce s ostatnými',
                'Emocionálnu stabilitu a ako zvládaš stres'
            ],
            howItWorks: [
                {
                    title: 'Odpovedz na 30 otázok',
                    description: 'Jednoduché tvrdenia, na ktoré odpovieš škálou od 1 do 5'
                },
                {
                    title: 'Získaj svoj profil',
                    description: 'Dostaneš percentilové skóre pre každú z 5 dimenzií'
                },
                {
                    title: 'Pochop svoje silné stránky',
                    description: 'Detailný popis toho, čo tvoje skóre znamená'
                }
            ],
            funFacts: [
                'Big Five model predpovedá pracovný výkon lepšie ako IQ testy',
                'Extraverzia nie je o tom, či si spoločenský, ale ako spracovávaš stimuly',
                'Vysoká otvorenosť súvisí s kreativitou aj politickým liberalizmom',
                'Svedomitosť je najsilnejší prediktor akademického úspechu'
            ],
            science: {
                basis: 'Model Big Five (OCEAN) bol vyvinutý nezávisle viacerými výskumnými tímami od 1960-tych rokov. Je založený na lexikálnej hypotéze - že najdôležitejšie osobnostné črty sú zakódované v jazyku.',
                researchers:
                    'Kľúčový prínos mali psychológovia ako Lewis Goldberg, Paul Costa a Robert McCrae. Model bol validovaný na miliónoch ľudí po celom svete.',
                usage: 'Big Five sa používa v HR, klinickej psychológii, výskume aj osobnom rozvoji. Je to zlatý štandard pre meranie osobnosti vo vedeckej komunite.'
            }
        },

        [SurveyTypes.KOHLBERG]: {
            title: 'Morálny vývoj',
            subtitle: 'Objavte, ako uvažujete o správnom a nesprávnom',
            icon: '⚖️',
            iconBgClass: 'bg-orange-100',
            duration: '15',
            questions: 20,
            description:
                'Kohlbergova teória morálneho vývoja je jedným z najvplyvnejších modelov v psychológii. Pomocou morálnych dilem odhalíte štruktúru svojho etického myslenia - od konkrétnych pravidiel až po univerzálne princípy spravodlivosti.',
            aboutText:
                'Lawrence Kohlberg z Harvardu revolucionizoval náš pohľad na morálku. Jeho 20-ročný výskum ukázal, že morálne uvažovanie sa vyvíja v predvídateľných štádiách - podobne ako kognitívny vývoj. Každé štádium predstavuje sofistikovanejší spôsob riešenia etických dilem.',
            insights: [
                'Vaše štádium morálneho uvažovania (predkonvenčné, konvenčné, postkonvenčné)',
                'Či sa rozhodujete na základe pravidiel, sociálnych noriem alebo princípov',
                'Ako vyvažujete osobné záujmy s potrebami spoločnosti',
                'Vašu schopnosť vidieť etické dilemy z viacerých perspektív',
                'Potenciál pre ďalší rozvoj morálneho uvažovania'
            ],
            howItWorks: [
                {
                    title: 'Prečítate morálne dilemy',
                    description: 'Klasické etické situácie bez jednoduchých odpovedí'
                },
                {
                    title: 'Vyberiete svoje riešenie',
                    description: 'Rozhodnete, čo by ste urobili a prečo'
                },
                {
                    title: 'Dostanete analýzu',
                    description: 'Zistíte svoje štádium morálneho vývoja'
                }
            ],
            funFacts: [
                'Kohlbergova teória je jednou z 10 najcitovanejších v psychológii',
                'Len 10-15% dospelých dosahuje postkonvenčné štádiá',
                'Morálny vývoj pokračuje aj v dospelosti - nie je fixný',
                'Gandhi a Martin Luther King sú príkladmi postkonvenčného myslenia',
                'Model ovplyvnil etické vzdelávanie na celom svete'
            ],
            science: {
                basis: 'Teória vychádza z Piagetovej práce o kognitívnom vývoji. Kohlberg študoval odpovede na morálne dilemy počas 20 rokov longitudinálneho výskumu na Harvarde.',
                researchers:
                    'Lawrence Kohlberg (Harvard, 1958) je zakladateľom modernej morálnej psychológie. Jeho práca inšpirovala tisíce štúdií a výskumných projektov.',
                usage: 'Zlatý štandard v etickom vzdelávaní, právnej psychológii, business etike a charakterovom rozvoji. Používa sa na školách, univerzitách aj v korporáciách.'
            },
            critique: {
                title: 'Dôležitá perspektíva: Carol Gilligan',
                description:
                    'Carol Gilligan, Kohlbergova študentka z Harvardu, priniesla zásadnú kritiku v knihe "In a Different Voice" (1982). Upozornila, že Kohlberg skúmal primárne mužov a jeho model uprednostňuje "etiku spravodlivosti" (abstraktné princípy) pred "etikou starostlivosti" (vzťahy a zodpovednosť). Gilligan ukázala, že morálka založená na starostlivosti o konkrétnych ľudí a udržiavaní vzťahov nie je "nižšia" - je to rovnako hodnotný spôsob etického uvažovania. Ak váš výsledok ukazuje dôraz na vzťahy a konkrétnu zodpovednosť, neznamená to menej rozvinutú morálku - môže to odrážať etiku starostlivosti, ktorú Kohlbergov model nedokáže plne zachytiť.'
            }
        },

        [SurveyTypes.SCHWARTZ]: {
            title: 'Hodnoty a priority',
            subtitle: 'Čo je pre vás v živote najdôležitejšie',
            icon: '💎',
            iconBgClass: 'bg-indigo-100',
            duration: '6',
            questions: 20,
            description:
                'Schwartzov test hodnôt identifikuje vaše základné životné priority. Zistíte, ktoré z 10 univerzálnych hodnôt vás najviac motivujú a usmerňujú vaše rozhodnutia.',
            aboutText:
                'Shalom Schwartz identifikoval 10 univerzálnych hodnôt, ktoré sa vyskytujú vo všetkých kultúrach sveta. Tento model pomáha pochopiť, prečo robíme rozhodnutia, ktoré robíme.',
            insights: [
                'Vaše top 3 životné hodnoty a priority',
                'Konflikt medzi vašimi hodnotami (napr. sloboda vs. bezpečnosť)',
                'Ako vaše hodnoty ovplyvňujú kariérne rozhodnutia',
                'Prečo máte konflikty s určitými ľuďmi',
                'Ktoré hodnoty potrebujete viac rozvíjať pre životnú rovnováhu'
            ],
            howItWorks: [
                {
                    title: 'Odpoviete na 20 otázok',
                    description: 'V bežných životných situáciách si vyberáte, čo je pre vás dôležitejšie'
                },
                {
                    title: 'Systém analyzuje vzorce',
                    description: 'Identifikuje vaše dominantné hodnotové orientácie'
                },
                {
                    title: 'Dostanete hodnotový profil',
                    description: 'Kruhový graf vašich 10 základných hodnôt'
                }
            ],
            funFacts: [
                'Existuje presne 10 univerzálnych hodnôt naprieč všetkými kultúrami',
                'Hodnoty sa formujú do 20. roku a potom zostávajú stabilné',
                'Susedné hodnoty v kruhu sa dopĺňajú, protiľahlé si odporujú',
                'Generácia Z má iné hodnotové priority ako Mileniáli',
                'Hodnoty predpovedajú politickú orientáciu presnejšie ako demografia'
            ],
            science: {
                basis: 'Teória je založená na 25 rokoch výskumu v 82 krajinách sveta. Vychádza z Rokeachovej práce o hodnotách a Maslowovej hierarchie potrieb.',
                researchers:
                    'Shalom Schwartz z Hebrejskej univerzity v Jeruzaleme. Teória bola overená na vzorke 200,000+ respondentov globálne.',
                usage: 'Používa sa v medzikultúrnej psychológii, marketingu, politológii, kariérnom poradenstve a tímovom koučingu.'
            }
        },

        [SurveyTypes.HOLLAND]: {
            title: 'Kariérne záujmy',
            subtitle: 'Nájdite svoju ideálnu profesijnú cestu',
            icon: '💼',
            iconBgClass: 'bg-green-100',
            duration: '15',
            questions: 72,
            description:
                'Hollandov test (RIASEC) vám pomôže objaviť, ktoré typy práce a pracovného prostredia vám najviac vyhovujú na základe vašich záujmov a osobnosti.',
            aboutText:
                'John Holland vytvoril model 6 typov osobnosti a pracovných prostredí. Najväčšia spokojnosť vzniká, keď sa typ osobnosti zhoduje s typom práce.',
            insights: [
                'Váš 3-písmenový Holland kód (napr. RIA, SEC)',
                'Konkrétne povolania, ktoré vám vyhovujú',
                'Ideálne pracovné prostredie pre vašu osobnosť',
                'Aktivity, ktoré vás nabíjajú energiou',
                'Prečo vás niektoré práce nudia alebo vyčerpávajú'
            ],
            howItWorks: [
                {
                    title: 'Ohodnotíte pracovné aktivity',
                    description: 'Či by vás bavili rôzne pracovné činnosti'
                },
                {
                    title: 'Určí sa váš typ',
                    description: 'Kombinácia 3 najsilnejších typov z RIASEC'
                },
                {
                    title: 'Dostanete kariérne odporúčania',
                    description: 'Zoznam povolaní zodpovedajúcich vášmu profilu'
                }
            ],
            funFacts: [
                '75% ľudí má kombináciu 2-3 dominantných typov, nie jeden čistý typ',
                'Umelecký typ (A) je najzriedkavejší - len 2% populácie',
                'Sociálny (S) a Podnikavý (E) typy zarábajú v priemere najviac',
                'Realistický typ (R) má najvyššiu pracovnú spokojnosť',
                'Holland kód sa používa v americkej armáde pre kariérne poradenstvo'
            ],
            science: {
                basis: 'Teória vznikla v 1959 a je založená na predpoklade, že kariérna voľba je prejavom osobnosti. Model bol testovaný na miliónoch ľudí.',
                researchers:
                    'John L. Holland, americký psychológ. Jeho práca bola rozšírená Gottfredsonom a Prediger o dimenzie dát-ľudia a veci-myšlienky.',
                usage: 'Štandardný nástroj kariérneho poradenstva na školách, univerzitách, úradoch práce a v HR. Základ systému O*NET.'
            }
        },

        [SurveyTypes.GROWTH_MINDSET]: {
            title: 'Rastové myslenie',
            subtitle: 'Ako pristupujete k výzvam a rastu',
            icon: '🌱',
            iconBgClass: 'bg-teal-100',
            duration: '5',
            questions: 15,
            description:
                'Test rastového myslenia meria, či veríte, že vaše schopnosti sa môžu rozvíjať (growth mindset) alebo sú fixné (fixed mindset). Tento prístup zásadne ovplyvňuje váš úspech.',
            aboutText:
                'Carol Dweck zo Stanfordu objavila, že presvedčenie o povahe našich schopností má väčší vplyv na úspech ako samotné schopnosti.',
            insights: [
                'Či máte rastové alebo fixné myslenie v rôznych oblastiach',
                'Ako reagujete na neúspech a kritiku',
                'Vašu ochotu učiť sa a vychádzať z komfortnej zóny',
                'Či vidíte výzvy ako príležitosti alebo hrozby',
                'Ako môžete zmeniť svoj mindset pre lepšie výsledky'
            ],
            howItWorks: [
                {
                    title: 'Vyplníte otázky o učení',
                    description: 'Ako vnímate inteligneciu, talent a schopnosti'
                },
                {
                    title: 'Zistíte svoj mindset profil',
                    description: 'V ktorých oblastiach máte aký typ myslenia'
                },
                {
                    title: 'Dostanete stratégie rastu',
                    description: 'Konkrétne techniky pre rozvoj growth mindsetu'
                }
            ],
            funFacts: [
                'Študenti s growth mindsetom majú o 30% lepšie výsledky',
                'Mindset sa dá zmeniť už za 8 týždňov tréningom',
                'Chvála za snahu (nie talent) rozvíja growth mindset u detí',
                'Michael Jordan mal extrémny growth mindset - neúspechy ho motivovali',
                'Firmy s growth mindset kultúrou rastú 2x rýchlejšie'
            ],
            science: {
                basis: 'Výskum vychádza z 30 rokov štúdií motivácie a výkonu. Dweck sledovala tisíce študentov a ich akademický vývoj.',
                researchers:
                    'Carol Dweck zo Stanford University. Jej kniha "Mindset" zmenila prístup k vzdelávaniu a rozvoju.',
                usage: 'Používa sa vo vzdelávaní, športe, firemnom prostredí, koučingu a terapii. Microsoft a Google majú growth mindset programy.'
            }
        },

        [SurveyTypes.VIA]: {
            title: 'Charakterové silné stránky',
            subtitle: 'Objavte svojich top 5 charakterových síl',
            icon: '💪',
            iconBgClass: 'bg-yellow-100',
            duration: '10',
            questions: 96,
            description:
                'Tento test identifikuje vašich 5 najsilnejších charakterových stránok z 24 silných stránok zoskupených do 6 cností. Používanie týchto stránok vedie k väčšej životnej spokojnosti.',
            aboutText:
                'Test je výsledkom najväčšieho projektu pozitívnej psychológie. 55 vedcov skúmalo, čo robí ľudí výnimočnými naprieč kultúrami a históriou.',
            insights: [
                'Vašich top 5 signature strengths (podpisové silné stránky)',
                'Ako využívať tieto sily v práci a vzťahoch',
                'Ktoré sily možno preháňate a stávajú sa slabosťami',
                'Kombinácie síl, ktoré vytvárajú vašu jedinečnosť',
                'Stratégie pre rozvoj menej vyvinutých síl'
            ],
            howItWorks: [
                {
                    title: 'Ohodnotíte 96 tvrdení',
                    description: 'O vašom správaní v rôznych situáciách'
                },
                {
                    title: 'Identifikujú sa vaše sily',
                    description: 'Zoradenie 24 charakterových síl podľa intenzity'
                },
                {
                    title: 'Dostanete akčný plán',
                    description: 'Ako využiť svoje sily pre lepší život'
                }
            ],
            funFacts: [
                'Ľudia používajúci svoje top sily sú 6x viac engaged v práci',
                'Existuje 5.1 milióna unikátnych kombinácií top 5 síl',
                'Najčastejšia sila globálne je Vďačnosť, najzriedkavejšia Sebakontrola',
                'Páry znalé svojich síl majú o 50% nižšiu rozvodovosť',
                'Znalosť síl zvyšuje produktivitu tímov o 12.5%'
            ],
            science: {
                basis: 'Projekt skúmal cnosti a sily naprieč 3000 rokmi literatúry, filozofie a náboženstiev. Identifikovali 6 univerzálnych cností a 24 síl.',
                researchers:
                    'Martin Seligman a Christopher Peterson viedli tím 55 medzinárodných vedcov. Projekt trval 3 roky a stál $3 milióny.',
                usage: 'Používa sa v pozitívnej psychológii, koučingu, HR, vzdelávaní a terapii. Základ pre silné stránky-založené intervencie.'
            }
        },

        [SurveyTypes.KEGAN]: {
            title: 'Dospelý vývoj',
            subtitle: 'Úroveň komplexnosti vášho myslenia',
            icon: '📈',
            iconBgClass: 'bg-cyan-100',
            duration: '12',
            questions: 20,
            description:
                'Keganov test meria vývojovú úroveň vášho vedomia - ako komplexne dokážete vnímať seba, vzťahy a svet. Vyššie úrovne umožňujú efektívnejšie riešiť zložité problémy.',
            aboutText:
                'Robert Kegan z Harvardu vytvoril teóriu vývojových štádií dospelého vedomia. Väčšina dospelých sa vyvíja aj po 30-tke, ale len málo dosiahne najvyššie úrovne.',
            insights: [
                'Vašu súčasnú vývojovú úroveň (2-5)',
                'Ako komplexne dokážete myslieť a riešiť problémy',
                'Čo limituje váš osobný a profesionálny rast',
                'Aké výzvy potrebujete pre ďalší vývojový skok',
                'Prečo niektoré situácie sú pre vás overwhelming'
            ],
            howItWorks: [
                {
                    title: 'Vyriešite vývojové dilemy',
                    description: 'Situácie testujúce komplexnosť myslenia'
                },
                {
                    title: 'Určí sa vaša úroveň',
                    description: 'Od socializovanej po self-transforming'
                },
                {
                    title: 'Dostanete vývojový plán',
                    description: 'Ako posunúť svoje vedomie na vyššiu úroveň'
                }
            ],
            funFacts: [
                'Len 7% dospelých dosiahne najvyššiu úroveň vývoja',
                '65% dospelých je na úrovni 3 (socializovaná myseľ)',
                'CEO úspešných firiem sú priemerne o 1 úroveň vyššie ako ostatní',
                'Prechod medzi úrovňami trvá priemerne 5-7 rokov',
                'Meditácia a terapia môžu urýchliť vývojový progres'
            ],
            science: {
                basis: 'Teória stavia na práci Piageta, Kohlberga a Loevingerovej. Kegan sledoval vývoj dospelých počas 40 rokov longitudinálneho výskumu.',
                researchers:
                    'Robert Kegan z Harvard Graduate School of Education. Jeho kniha "In Over Our Heads" opisuje vývojové výzvy moderného sveta.',
                usage: 'Používa sa v leadership developmente, executive koučingu, terapii a organizačnom rozvoji. Základ pre Deliberately Developmental Organizations.'
            }
        },

        [SurveyTypes.ATTACHMENT]: {
            title: 'Vzťahové štýly',
            subtitle: 'Ako vytvárate a udržiavate blízke vzťahy',
            icon: '❤️',
            iconBgClass: 'bg-pink-100',
            duration: '6',
            questions: 16,
            description:
                'Test vzťahovej väzby odhalí váš štýl vytvárania blízkych vzťahov. Pochopíte svoje vzorce v láske, priateľstve a prečo sa vo vzťahoch správate, ako sa správate.',
            aboutText:
                'Teória vzťahovej väzby vysvetľuje, ako raná skúsenosť s rodičmi formuje našu schopnosť vytvárať blízke vzťahy v dospelosti.',
            insights: [
                'Váš primárny vzťahový štýl (bezpečný, úzkostný, vyhýbavý, bojazlivo-vyhýbavý)',
                'Prečo máte určité vzorce vo vzťahoch',
                'Ako váš štýl ovplyvňuje výber partnerov',
                'Stratégie pre zdravšie vzťahové vzorce',
                'Kompatibilita s inými vzťahovými štýlmi'
            ],
            howItWorks: [
                {
                    title: 'Odpoviete na otázky o vzťahoch',
                    description: 'Ako sa cítite v blízkych vzťahoch'
                },
                {
                    title: 'Identifikuje sa váš štýl',
                    description: 'Miera úzkosti a vyhýbavosti vo vzťahoch'
                },
                {
                    title: 'Dostanete vzťahový profil',
                    description: 'Detailný popis vášho štýlu a odporúčania'
                }
            ],
            funFacts: [
                '60% ľudí má bezpečný vzťahový štýl, 40% má neistý',
                'Vzťahový štýl sa dá zmeniť cez "earned security" v terapii',
                'Partneri s rovnakým štýlom majú 2x vyššiu spokojnosť',
                'Úzkostný + vyhýbavý vytvárajú najdramatickejšie vzťahy',
                'Vzťahový štýl predpovedá aj pracovné vzťahy a leadership'
            ],
            science: {
                basis: 'Bowlby a Ainsworth vytvorili teóriu na základe štúdia detí a matiek. Hazan a Shaver ju rozšírili na dospelé vzťahy.',
                researchers:
                    'John Bowlby, Mary Ainsworth, Cindy Hazan a Phillip Shaver. Main a Solomon pridali dezorganizovaný štýl.',
                usage: 'Základ párovej terapie, používa sa v klinickej praxi, adopčných procesoch, HR pre tímovú dynamiku.'
            }
        },

        [SurveyTypes.SDT]: {
            title: 'Motivácia',
            subtitle: 'Preskúmaj, čo ťa poháňa v rôznych oblastiach',
            icon: '🔥',
            iconBgClass: 'bg-red-100',
            duration: '3',
            questions: 15,
            description: 'Krátka reflexia práce alebo štúdia, učenia a pomoci druhým. Každý dôvod ohodnotíš zvlášť — viaceré môžu sedieť súčasne.',
            aboutText: 'Motivácia sa môže líšiť podľa činnosti aj aktuálnej situácie. Oblasť, ktorá sa ťa momentálne netýka, môžeš preskočiť.',
            insights: [
                'Tvoje dôvody v práci alebo pri štúdiu',
                'Čo ťa privádza k učeniu sa nových vecí',
                'Akú úlohu majú odmeny, povinnosť, význam a radosť',
                'Motivačná mapa podľa oblastí'
            ],
            howItWorks: [
                { title: 'Vyber si konkrétnu skúsenosť', description: 'Premýšľaj o tom, čo robíš v poslednom čase.' },
                { title: 'Ohodnoť každý dôvod od 1 do 5', description: 'Niekoľko dôvodov môže platiť súčasne. Oblasti, ktoré sa ťa netýkajú, preskoč.' },
                { title: 'Pozri sa na svoju mapu', description: 'Výsledok ukáže tvoje hodnotenia osobitne pre každú oblasť.' }
            ],
            funFacts: [
                'Tá istá činnosť môže mať pre teba viac dôvodov.',
                'Práca, učenie aj pomoc druhým môžu mať odlišnú motiváciu.',
                'Tvoje dôvody sa môžu s novou skúsenosťou zmeniť.'
            ],
            science: {
                basis: 'Vlastná reflexia GROWNi inšpirovaná teóriou sebaurčenia (SDT). Nie je psychometricky validovaným testom ani diagnostickým nástrojom.',
                researchers: 'Teóriu SDT rozvinuli Edward Deci a Richard Ryan. Tieto otázky a výsledky vytvorilo GROWNi.',
                usage: 'Na osobné zamyslenie a rozhovor o motivácii. Výsledok nemeria pohodu ani naplnenie psychologických potrieb.'
            }
        },

        [SurveyTypes.ENTREPRENEURSHIP]: {
            title: 'Test podnikavosti',
            subtitle: 'Objavte svoj podnikateľský potenciál',
            icon: '🚀',
            iconBgClass: 'bg-amber-100',
            duration: '3',
            questions: 35,
            description:
                'Test podnikavosti meria vaše predpoklady pre podnikanie a inovatívne myslenie. Zistíte, či máte vlastnosti úspešného podnikateľa a v ktorých oblastiach môžete rásť.',
            aboutText:
                'Tento test je založený na výskume charakteristík úspešných podnikateľov a startupistov. Kombinuje klasické podnikateľské vlastnosti s modernými konceptami ako je lean startup metodológia a growth mindset.',
            insights: [
                'Vašu schopnosť identifikovať a využiť príležitosti',
                'Ochotu riskovať a vychádzať z komfortnej zóny',
                'Inovatívne myslenie a kreativitu v riešení problémov',
                'Vytrvalosť a odolnosť voči neúspechu',
                'Komunitnú angažovanosť a sociálnu zodpovednosť',
                'Ochotu zapájať sa do verejného a demokratického diania'
            ],
            howItWorks: [
                {
                    title: 'Vyplníte 35 otázok',
                    description: 'O vašom prístupe k riziku, inovácii a vedeniu'
                },
                {
                    title: 'Získate podnikateľský profil',
                    description: 'Detailné hodnotenie vašich podnikateľských vlastností'
                },
                {
                    title: 'Dostanete odporúčania',
                    description: 'Konkrétne tipy pre rozvoj podnikateľských schopností'
                }
            ],
            funFacts: [
                '92% úspešných podnikateľov má vysokú toleranciu nejednoznačnosti',
                'Priemerný vek úspešného startup zakladateľa je 45 rokov, nie 25',
                'Vytrvalosť (grit) je silnejší prediktor úspechu ako IQ',
                'Podnikatelia majú o 50% vyššiu pravdepodobnosť dyslexie',
                '70% startupov zlyhá kvôli tímovým problémom, nie kvôli produktu'
            ],
            science: {
                basis: 'Test vychádza z práce výskumníkov ako Howard Stevenson z Harvard Business School a moderných konceptov od Eric Ries (Lean Startup) a Steve Blank (Customer Development).',
                researchers:
                    'Kombinuje klasický výskum podnikania (Schumpeter, McClelland) s modernými prístupmi (Sarasvathy - effectuation).',
                usage: 'Používa sa v inkubátoroch, akcelerátoroch, na univerzitách, v korporátnom prostredí pre intrapreneurship programy.'
            }
        },

        [SurveyTypes.EXCELLENCE]: {
            title: 'Index excelencie',
            subtitle: 'Merajte svoj potenciál pre výnimočnosť',
            icon: '🏆',
            iconBgClass: 'bg-yellow-100',
            duration: '3',
            questions: 30,
            description:
                'Index excelencie meria vašu kapacitu dosahovať výnimočné výsledky. Kombinuje faktory ako disciplína, štandardy kvality, neustále zlepšovanie a snahu o majstrovstvo.',
            aboutText:
                'Tento test integruje výskum excelencie z rôznych oblastí - od športu cez umenie po vedu. Meria faktory, ktoré odlišujú top performerov od priemeru.',
            insights: [
                'Vaša úroveň zaväzuje k excelencii',
                'Vedomé praktizovanie vs. prirodzený talent',
                'Schopnosť stanoviť si vysoké štandardy',
                'Vytrvalosť pri dosahovaní majstrovstva',
                'Growth mindset a kontinuálne zlepšovanie',
                'Rovnováhu medzi perfekcionizmom a pragmatizmom'
            ],
            howItWorks: [
                {
                    title: 'Zhodnotíte prístup k excelencii',
                    description: 'V rôznych oblastiach života a práce'
                },
                {
                    title: 'Získate index excelencie',
                    description: 'Skóre v 5 dimenziách a celkový index od 0 do 100'
                },
                {
                    title: 'Dostanete rozvojový plán',
                    description: 'Cesta k osobnej a profesionálnej excelencii'
                }
            ],
            funFacts: [
                '10,000 hodín vedomého praktizovania je mýtus - kvalita tréningu je dôležitejšia',
                'Top performeri trávia 3-5 hodín denne v hlbokej práci',
                'Excelencia je 25% talent a 75% systematický rozvoj',
                '1% denné zlepšenie = 37x lepší za rok',
                'Výnimočný výkon vyžaduje 7-9 hodín spánku'
            ],
            science: {
                basis: 'Integruje výskum od Ericssona (deliberate practice), Duckworth (grit), Pink (drive) a Csikszentmihalyiho (flow).',
                researchers:
                    'Anders Ericsson, Angela Duckworth, Cal Newport, Josh Waitzkin. Výskum výnimočného výkonu a expertízy.',
                usage: 'Používa sa v športe, umení, korporátnom prostredí, vzdelávaní talentov a osobnom koučingu.'
            }
        },

        [SurveyTypes.HELP_SLOVAKIA]: {
            title: 'Chuť pomôcť Slovensku',
            subtitle: 'Ako môžete prispieť k lepšej spoločnosti',
            icon: '🇸🇰',
            iconBgClass: 'bg-sky-100',
            duration: '8',
            questions: 20,
            description:
                'Test občianskej angažovanosti meria vašu ochotu a spôsoby, ako prispievať k pozitívnej zmene na Slovensku. Zistíte, kde môže mať váš príspevok najväčší dopad.',
            aboutText:
                'Slovensko potrebuje aktívnych občanov. Tento test vám pomôže nájsť oblasti, kde vaše schopnosti a záujmy môžu priniesť najväčšiu pozitívnu zmenu.',
            insights: [
                'Oblasti, kde môžete najviac pomôcť',
                'Váš štýl občianskej angažovanosti',
                'Konkrétne spôsoby, ako sa zapojiť',
                'Organizácie a iniciatívy, ktoré vám vyhovujú',
                'Ako prekonať bariéry v angažovanosti'
            ],
            howItWorks: [
                {
                    title: 'Vyplníte krátky dotazník',
                    description: 'O vašich záujmoch a možnostiach pomáhať'
                },
                {
                    title: 'Identifikujú sa vaše silné stránky',
                    description: 'Kde môžete mať najväčší dopad'
                },
                {
                    title: 'Dostanete akčný plán',
                    description: 'Konkrétne kroky pre zapojenie sa'
                }
            ],
            funFacts: [
                'Len 27% Slovákov je aktívnych v občianskej spoločnosti',
                'Dobrovoľníctvo zvyšuje pocit šťastia o 40%',
                '2 hodiny pomoci týždenne = optimum pre well-being',
                'Slovensko je na 49. mieste v Indexe dobrovoľníctva',
                'Mladí do 30 sú najaktívnejší v online aktivizme'
            ],
            science: {
                basis: 'Test vychádza z výskumu občianskej angažovanosti, social impact theory a community psychology.',
                researchers:
                    'Inšpirované prácou Putnama (social capital), Granovettera (sila slabých väzieb) a lokálnym výskumom.',
                usage: 'Používa sa NGO sektorom, v občianskom vzdelávaní, CSR programoch a komunitnom organizovaní.'
            }
        },

        [SurveyTypes.SELF_KNOWLEDGE]: {
            title: 'Osobnostný dotazník',
            subtitle: 'Kompletný profil vašej osobnosti',
            icon: '🧠',
            iconBgClass: 'bg-purple-100',
            duration: '15',
            questions: 92,
            description:
                'Najkomplexnejší test, ktorý kombinuje viacero psychologických modelov. Získate hlboký vhľad do svojej osobnosti, motivácií a potenciálu.',
            aboutText:
                'Tento dotazník integruje najlepšie z viacerých psychologických teórií do jedného komplexného profilu. Je to váš osobný psychologický pas.',
            insights: [
                'Multi-dimenzionálny osobnostný profil',
                'Silné a slabé stránky v rôznych kontextoch',
                'Ideálne pracovné prostredie a roly',
                'Kompatibilita s rôznymi typmi ľudí',
                'Potenciálne riziká a príležitosti pre rast',
                'Personalizované stratégie rozvoja'
            ],
            howItWorks: [
                {
                    title: 'Vyplníte 92 otázok',
                    description: 'Pokrývajúcich všetky aspekty osobnosti'
                },
                {
                    title: 'Vyhodnotíme odpovede',
                    description: 'Spočítame skóre v ôsmich oblastiach osobnosti'
                },
                {
                    title: 'Dostanete osobnostný profil',
                    description: 'Výsledok v každej oblasti s krátkou interpretáciou'
                }
            ],
            funFacts: [
                'Kombinuje 5+ overených psychologických modelov',
                'Presnosť predpovede správania je 78%',
                'Report obsahuje 50+ personalizovaných insightov',
                'Používa machine learning pre hlbšie vzorce',
                'Aktualizuje sa s novými vedeckými poznatkami'
            ],
            science: {
                basis: 'Integruje Big Five, HEXACO, hodnoty, motivácie a ďalšie modely do unified framework.',
                researchers:
                    'Kombinuje viacero psychologických škál vrátane kontrolnej škály úprimnosti odpovedí.',
                usage: 'Používa sa pre komplexné self-assessment, kariérne plánovanie, osobný rozvoj a koučing.'
            }
        },

        // Placeholder for HELP_FROM_GROWNI if needed
        [SurveyTypes.HELP_FROM_GROWNI]: {
            title: 'Pomoc od GROWNi',
            subtitle: 'Ako vám môžeme pomôcť rásť',
            icon: '🤝',
            iconBgClass: 'bg-blue-100',
            duration: '5',
            questions: 10,
            description:
                'Krátky dotazník, ktorý nám pomôže pochopiť vaše potreby a navrhnúť najlepšie riešenia pre váš osobný a profesionálny rast.',
            aboutText:
                'GROWNi je tu pre vás. Tento dotazník nám pomôže personalizovať našu podporu presne podľa vašich potrieb.',
            insights: [
                'Oblasti, kde potrebujete najviac podpory',
                'Odporúčané programy a workshopy',
                'Mentori a kouči vhodní pre vás',
                'Komunity a skupiny s podobnými záujmami',
                'Personalizovaný rozvojový plán'
            ],
            howItWorks: [
                {
                    title: 'Popíšete svoje ciele',
                    description: 'Čo chcete dosiahnuť a kde potrebujete pomoc'
                },
                {
                    title: 'Systém vyhodnotí možnosti',
                    description: 'Nájde najlepšie riešenia z našej ponuky'
                },
                {
                    title: 'Dostanete personalizované odporúčania',
                    description: 'Konkrétne kroky a kontakty'
                }
            ],
            funFacts: [
                '87% užívateľov našlo mentora do 2 týždňov',
                'Priemerné zlepšenie soft skills o 40% za 3 mesiace',
                'Najžiadanejší workshop: Impostor syndrome',
                '500+ úspešných mentoring matchov',
                'Community events každý týždeň'
            ],
            science: {
                basis: 'Založené na personalized learning theory a strengths-based development.',
                researchers: 'Využíva best practices z EdTech a talent development.',
                usage: 'Interný nástroj GROWNi pre personalizáciu služieb a podpory.'
            }
        },

        [SurveyTypes.LEADERSHIP_STYLES]: {
            title: 'Štýly vedenia',
            subtitle: 'Objavte svoj dominantný štýl vedenia podľa Golemana',
            icon: '👔',
            iconBgClass: 'bg-purple-100',
            duration: '10',
            questions: 20,
            description:
                'Daniel Goleman identifikoval 6 štýlov vedenia na základe emocionálnej inteligencie. Žiadny štýl nie je najlepší – najúčinnejší lídri flexibilne kombinujú viaceré. Zistite, ktorý je váš dominantný.',
            aboutText:
                'Test je založený na výskume Daniela Golemana s databázou 3 871 manažérov, ktorý ukázal, že štýl vedenia priamo ovplyvňuje organizačnú klímu a vysvetľuje až 70 % variácie vo výkone tímu.',
            insights: [
                'Tvoj dominantný štýl vedenia z 6 Golemanových štýlov',
                'Ako tvoj štýl ovplyvňuje klímu a výkon tímu',
                'Kedy je tvoj štýl najúčinnejší a kedy riskantný',
                'Konkrétne odporúčania na rozvoj flexibilného vedenia',
                'Porovnanie s ostatnými štýlmi a ich výhody'
            ],
            howItWorks: [
                {
                    title: 'Odpovedz na 20 situačných otázok',
                    description: 'Vyber reakciu, ktorá ti je najprirodzenejšia'
                },
                {
                    title: 'Získaj svoj profil vedenia',
                    description: 'Dominantný štýl + rozloženie cez všetkých 6 štýlov'
                },
                {
                    title: 'Dostaneš odporúčania na rozvoj',
                    description: 'Ako rozvíjať flexibilitu a využívať silné stránky'
                }
            ],
            funFacts: [
                'Len 10 % lídrov prirodzene používa viac ako 2 štýly',
                'Vizionársky štýl má najsilnejší pozitívny dopad na klímu',
                'Pacesetting a commanding najčastejšie „otrávia" tímovú klímu',
                'Emocionálna inteligencia vysvetľuje 2× viac variácie vo výkone lídra ako IQ'
            ],
            science: {
                basis: 'Výskum Golemana s Hay Group (teraz Korn Ferry) na vzorke 3 871 manažérov v 6 krajinách, publikovaný v Harvard Business Review (2000) a Primal Leadership (2002).',
                researchers:
                    'Daniel Goleman – psychológ, autor bestselleru Emotional Intelligence (1995). Spolupráca s Richardom Boyatzisom a Annie McKee.',
                usage: 'Executive coaching, leadership development, 360° hodnotenia, MBA programy, firemné tréningy po celom svete.'
            }
        },

        [SurveyTypes.LALOUX_PARADIGMS]: {
            title: 'Organizačná kultúra',
            subtitle: 'V akej farbe operuje tvoja organizácia?',
            icon: '🏢',
            iconBgClass: 'bg-teal-100',
            duration: '8',
            questions: 20,
            description:
                'Frédéric Laloux identifikoval 5 vývojových paradigiem organizácií – od červenej (moc) po tyrkysovú (živý organizmus). Každá farba má svoje silné stránky aj limity.',
            aboutText:
                'Laloux vo svojej knihe Reinventing Organizations (2014) ukázal, že organizácie prechádzajú vývojovými štádiami podobne ako ľudia. Pochopenie vašej paradigmy je prvý krok k vedomej transformácii.',
            insights: [
                'Organizačná paradigma, v ktorej sa najčastejšie pohybuješ',
                'Ako tvoja organizácia rozhoduje, motivuje a rieši konflikty',
                'Silné stránky a riziká tvojej aktuálnej paradigmy',
                'Porovnanie všetkých 5 farieb Lalouxovho modelu',
                'Odporúčania na posun organizačného vedomia'
            ],
            howItWorks: [
                {
                    title: 'Odpovedz na 20 otázok',
                    description: 'O rozhodovaní, motivácii a kultúre tvojej organizácie'
                },
                {
                    title: 'Získaj farebnú paradigmu',
                    description: 'Dominantná farba + rozloženie cez všetkých 5 paradigiem'
                },
                { title: 'Pochop svoju organizáciu', description: 'Čo je silné, čo je rizikové a kam sa dá rásť' }
            ],
            funFacts: [
                'Buurtzorg má 15 000 zamestnancov a žiadny stredný manažment',
                'Väčšina svetových firiem operuje na oranžovej úrovni',
                'Organizácia sa nemôže posunúť vyššie, než je vedomie jej lídra',
                'Morning Star nemá žiadnych manažérov – 400+ zamestnancov sa riadi samo'
            ],
            science: {
                basis: 'Laloux staval na vývojovej psychológii (Kegan, Wilber, Graves, Beck & Cowan – Spiral Dynamics) a výskume 12 organizácií operujúcich na tyrkysovej úrovni.',
                researchers:
                    'Frédéric Laloux – bývalý konzultant McKinsey, autor Reinventing Organizations, jedného z najvplyvnejších manažérskych textov poslednej dekády.',
                usage: 'Organizačný dizajn, kultúrna transformácia, agile transformácia, self-management, holacracy, sociokracia.'
            }
        },

        [SurveyTypes.KEGAN_ORDERS]: {
            title: 'Úrovne vedomia',
            subtitle: 'Ako konštruuješ zmysel sveta podľa Roberta Kegana',
            icon: '🧬',
            iconBgClass: 'bg-indigo-100',
            duration: '10',
            questions: 20,
            description:
                'Robert Kegan identifikoval 5 úrovní kognitívnej komplexity. Tento test pokrýva úrovne 2–5 a pomôže ti pochopiť, ako konštruuješ zmysel sveta, vzťahov a vlastnej identity.',
            aboutText:
                'Keganová teória je jednou z najvplyvnejších v oblasti adult development. Väčšina dospelých sa nachádza na úrovni 3 (socializovaná myseľ) – čo je normálne. Pochopenie svojej úrovne ti pomôže cielene sa rozvíjať.',
            insights: [
                'Tvoja primárna úroveň kognitívnej komplexity',
                'Ako konštruuješ zmysel sveta a vzťahov',
                'Tvoj vzťah k autorite, kritike a identite',
                'Rozdiely medzi úrovňami dospelého vývoja',
                'Praktiky na posun na ďalšiu úroveň'
            ],
            howItWorks: [
                {
                    title: 'Odpovedz na 20 hlbokých otázok',
                    description: 'Vyber odpoveď, ktorá najúprimnejšie vystihuje tvoje myslenie'
                },
                { title: 'Získaj svoju úroveň', description: 'Dominantná úroveň + rozloženie cez všetky úrovne' },
                { title: 'Pochop svoju cestu', description: 'Kde si teraz a ako sa posunúť ďalej' }
            ],
            funFacts: [
                'Približne 65 % dospelých operuje na úrovni 3 (socializovaná myseľ)',
                'Len asi 35 % dospelých dosiahne úroveň 4 (seba-autorská myseľ)',
                'Úroveň 5 je vzácna – typicky sa objavuje po 40. roku života',
                'Väčšina pracovných pozícií dnes vyžaduje úroveň 4, ale väčšina ľudí je na 3'
            ],
            science: {
                basis: 'Kegan nadväzuje na Piageta a Kohlberga. Teória je validovaná Subject-Object Interview (SOI) – štandardizovaným 90-minútovým rozhovorom.',
                researchers:
                    'Robert Kegan – profesor Harvard Graduate School of Education, autor In Over Our Heads (1994) a Immunity to Change (2009).',
                usage: 'Executive coaching, leadership development, organizačný vývoj, psychoterapia, vzdelávanie dospelých, Deliberately Developmental Organizations.'
            }
        },

        [SurveyTypes.LISTENING_LEVELS]: {
            title: 'Úrovne počúvania',
            subtitle: 'Na akej úrovni skutočne počúvaš?',
            icon: '👂',
            iconBgClass: 'bg-sky-100',
            duration: '6',
            questions: 20,
            description:
                'Co-Active Training Institute rozlíšil 3 úrovne počúvania. Väčšina ľudí trávi väčšinu času na Level 1 (interné počúvanie). Uvedomenie si svojej úrovne je prvý krok k hlbšiemu spojeniu.',
            aboutText:
                'Model 3 úrovní počúvania pochádza z Co-Active koučingového modelu – jedného z najpoužívanejších koučingových frameworkov na svete s 40+ rokmi praxe. Úrovne nie sú lepšie alebo horšie – každá má svoje miesto.',
            insights: [
                'Tvoja primárna úroveň počúvania',
                'Ako tvoj štýl počúvania ovplyvňuje vzťahy',
                'Rozdiel medzi počúvaním seba, druhého a celého poľa',
                'Praktické cvičenia na rozvoj hlbšieho počúvania',
                'Kedy je každá úroveň najvhodnejšia'
            ],
            howItWorks: [
                { title: 'Odpovedz na 20 otázok', description: 'O tom, čo sa deje v tvojej hlave počas rozhovorov' },
                { title: 'Získaj svoju úroveň', description: 'Dominantná úroveň + rozloženie cez 3 úrovne' },
                { title: 'Dostaneš praktické tipy', description: 'Konkrétne cvičenia na rozvoj počúvania' }
            ],
            funFacts: [
                'Priemerne si zapamätáme len 25–50 % z toho, čo počujeme',
                'Väčšina ľudí začne formulovať odpoveď po 12 sekundách počúvania',
                'Aktívne počúvanie zvyšuje dôveru v tíme o 40 %',
                'Level 3 je kľúčovou kompetenciou profesionálnych koučov'
            ],
            science: {
                basis: 'Model pochádza z Co-Active koučingového modelu, ktorý je certifikovaný ICF (International Coach Federation) a používaný v 40+ krajinách.',
                researchers:
                    'Henry a Karen Kimsey-House, Laura Whitworth, Phillip Sandahl – zakladatelia CTI, najväčšej koučingovej organizácie na svete.',
                usage: 'Koučing, leadership development, facilitácia, mediácia, partnerské vzťahy, vyjednávanie, manažérske rozhovory.'
            }
        },

        [SurveyTypes.RESILIENCE]: {
            title: 'Dotazník odolnosti',
            subtitle: 'Ako zvládaš výzvy a stres',
            icon: '🛡️',
            iconBgClass: 'bg-gray-100',
            duration: '10',
            questions: 20,
            description: 'Dotazník odolnosti - pripravujeme.',
            aboutText: 'Pripravujeme.',
            insights: [],
            howItWorks: [],
            funFacts: [],
            science: { basis: '', researchers: '', usage: '' }
        }
    }

    const getContent = (surveyType: SurveyTypes): TestLandingContent => {
        return content[surveyType] || content[SurveyTypes.SELF_KNOWLEDGE]
    }

    const getContentBySlug = (slug: string): TestLandingContent | null => {
        // Map slugs to survey types
        const slugToType: Record<string, SurveyTypes> = {
            'big-five': SurveyTypes.BIG_FIVE,
            'moralny-vyvoj': SurveyTypes.KOHLBERG,
            'hodnoty': SurveyTypes.SCHWARTZ,
            'karierne-zaujmy': SurveyTypes.HOLLAND,
            'rastove-myslenie': SurveyTypes.GROWTH_MINDSET,
            'charakterove-silne-stranky': SurveyTypes.VIA,
            'dospely-vyvoj': SurveyTypes.KEGAN,
            'vztahove-styly': SurveyTypes.ATTACHMENT,
            'motivacia': SurveyTypes.SDT,
            'podnikavost': SurveyTypes.ENTREPRENEURSHIP,
            'index-excelencie': SurveyTypes.EXCELLENCE,
            'chut-pomoct-slovensku': SurveyTypes.HELP_SLOVAKIA,
            'osobnostny-dotaznik': SurveyTypes.SELF_KNOWLEDGE,
            'pomoc-od-growni': SurveyTypes.HELP_FROM_GROWNI,
            'styly-vedenia': SurveyTypes.LEADERSHIP_STYLES,
            'organizacne-paradigmy': SurveyTypes.LALOUX_PARADIGMS,
            'urovne-vedomia': SurveyTypes.KEGAN_ORDERS,
            'urovne-pocuvania': SurveyTypes.LISTENING_LEVELS
        }

        const type = slugToType[slug]
        return type ? content[type] : null
    }

    return {
        getContent,
        getContentBySlug
    }
}
